// Inclusion+AI - Premium Document System
// Builds elegant, formal PDF / print outputs with consistent header/footer/watermark.
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const PLATFORM = "Inclusion+AI";
const PLATFORM_AR = "إنكلوجن بلس إيه آي";
const TAGLINE = "منصة الدمج التعليمي الذكي · AI-Powered Inclusive Education";

const ACCREDITATION_BADGE_SVG = `
<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" style="width:64px;height:64px">
  <defs>
    <linearGradient id="goldgrad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#D4AF37"/>
      <stop offset="50%" stop-color="#F4E5A0"/>
      <stop offset="100%" stop-color="#B8860B"/>
    </linearGradient>
  </defs>
  <circle cx="50" cy="50" r="46" fill="none" stroke="url(#goldgrad)" stroke-width="3"/>
  <circle cx="50" cy="50" r="36" fill="url(#goldgrad)" opacity="0.18"/>
  <circle cx="50" cy="50" r="36" fill="none" stroke="url(#goldgrad)" stroke-width="1"/>
  <text x="50" y="48" text-anchor="middle" font-family="Georgia,serif" font-size="11" font-weight="700" fill="#2F5233">Inclusion</text>
  <text x="50" y="62" text-anchor="middle" font-family="Georgia,serif" font-size="14" font-weight="700" fill="#D4AF37">+AI</text>
  <g stroke="#D4AF37" stroke-width="1.5" fill="none">
    <path d="M50,8 L52,4 L48,4 Z" fill="#D4AF37"/>
    <path d="M50,92 L52,96 L48,96 Z" fill="#D4AF37"/>
    <path d="M8,50 L4,52 L4,48 Z" fill="#D4AF37"/>
    <path d="M92,50 L96,52 L96,48 Z" fill="#D4AF37"/>
  </g>
</svg>`;

// Common CSS injected into every printable document
const SHARED_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800;900&family=Playfair+Display:wght@600;700;800&display=swap');
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  html, body { margin: 0; padding: 0; background: #fff; color: #1A201E; font-family: 'Cairo', 'Segoe UI', sans-serif; }
  body { font-size: 13px; line-height: 1.65; direction: rtl; }
  .doc { max-width: 800px; margin: 0 auto; background: #fff; }
  .doc-header {
    display: flex; justify-content: space-between; align-items: flex-start;
    padding: 28px 36px 18px; border-bottom: 3px double #2F5233;
    background: linear-gradient(180deg, #FAFAF6 0%, #fff 100%);
  }
  .brand { display: flex; align-items: center; gap: 14px; }
  .brand-text h1 { margin: 0; font-family: 'Playfair Display', serif; font-size: 26px; color: #2F5233; letter-spacing: 0.5px; }
  .brand-text p  { margin: 2px 0 0; font-size: 10px; color: #6b6b6b; letter-spacing: 0.8px; text-transform: uppercase; }
  .doc-meta { text-align: left; font-size: 10px; color: #6b6b6b; line-height: 1.6; }
  .doc-meta .ref { font-family: 'Courier New', monospace; color: #2F5233; font-weight: 700; }
  .doc-title-band {
    background: linear-gradient(90deg, #2F5233 0%, #517A55 50%, #2F5233 100%);
    color: #fff; padding: 18px 36px; text-align: center; position: relative;
  }
  .doc-title-band::before, .doc-title-band::after {
    content: ""; position: absolute; top: 50%; transform: translateY(-50%);
    width: 60px; height: 1px; background: rgba(255,255,255,0.4);
  }
  .doc-title-band::before { right: 36px; } .doc-title-band::after { left: 36px; }
  .doc-title-band h2 { margin: 0; font-family: 'Playfair Display', serif; font-size: 22px; font-weight: 700; letter-spacing: 1px; }
  .doc-title-band p { margin: 4px 0 0; font-size: 11px; opacity: 0.85; }
  .doc-body { padding: 28px 36px; position: relative; }
  .doc-body::before {
    content: "Inclusion+AI"; position: absolute; top: 50%; left: 50%;
    transform: translate(-50%, -50%) rotate(-22deg); font-family: 'Playfair Display', serif;
    font-size: 110px; color: rgba(47,82,51,0.04); pointer-events: none; white-space: nowrap; z-index: 0; font-weight: 800;
  }
  .doc-body > * { position: relative; z-index: 1; }

  .section { margin-bottom: 22px; }
  .section h3 { color: #2F5233; font-family: 'Playfair Display', serif; font-size: 17px; font-weight: 700;
    margin: 0 0 10px; padding-bottom: 6px; border-bottom: 1px solid #E5E2DB; display: flex; align-items: center; gap: 8px; }
  .section h3::before { content: ""; width: 6px; height: 18px; background: linear-gradient(180deg, #D4AF37 0%, #B8860B 100%); border-radius: 2px; }
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; }

  .info-row { display: flex; gap: 8px; padding: 6px 0; border-bottom: 1px dashed #E5E2DB; font-size: 12px; }
  .info-row .label { color: #6b6b6b; font-weight: 600; min-width: 110px; }
  .info-row .value { color: #1A201E; flex: 1; }

  table.report { width: 100%; border-collapse: collapse; margin: 6px 0; font-size: 12px; }
  table.report th, table.report td { border: 1px solid #E5E2DB; padding: 8px 10px; text-align: right; vertical-align: top; }
  table.report th { background: #F0EFEB; color: #2F5233; font-weight: 700; }
  table.report tr:nth-child(even) td { background: #FAFAF6; }

  .pill { display: inline-block; padding: 3px 10px; border-radius: 12px; font-size: 10px; font-weight: 600; margin: 1px; }
  .pill-emerald { background: #d1fae5; color: #065f46; }
  .pill-amber { background: #fef3c7; color: #92400e; }
  .pill-rose { background: #ffe4e6; color: #9f1239; }
  .pill-blue { background: #dbeafe; color: #1e40af; }
  .pill-gold { background: linear-gradient(135deg, #FBE69A 0%, #D4AF37 100%); color: #5C4400; }

  .quote { background: #FAFAF6; border-right: 4px solid #D4AF37; padding: 10px 14px; font-style: italic; color: #4A5553; margin: 10px 0; border-radius: 4px; }

  .signatures { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 24px; margin-top: 30px; padding-top: 18px; border-top: 1px solid #E5E2DB; }
  .sig-block { text-align: center; }
  .sig-line { border-bottom: 1px solid #2F5233; height: 36px; }
  .sig-block .role { font-size: 11px; color: #2F5233; font-weight: 700; margin-top: 4px; }
  .sig-block .name { font-size: 10px; color: #6b6b6b; }

  .doc-footer { padding: 14px 36px; border-top: 2px solid #2F5233; background: #FAFAF6;
    display: flex; justify-content: space-between; align-items: center; font-size: 9px; color: #6b6b6b; }
  .doc-footer .platform { font-family: 'Playfair Display', serif; color: #2F5233; font-weight: 700; }

  .seal { position: absolute; right: 36px; bottom: 90px; transform: rotate(-12deg); opacity: 0.95; z-index: 2; }

  /* Certificate-specific */
  .cert-frame {
    border: 12px solid transparent;
    border-image: linear-gradient(135deg, #D4AF37, #F4E5A0, #B8860B, #D4AF37) 1;
    padding: 0; position: relative; background: #FFFEF9;
  }
  .cert-inner { padding: 50px 60px; text-align: center; position: relative; }
  .cert-inner::before {
    content: ""; position: absolute; inset: 14px; border: 1px solid rgba(212,175,55,0.5); pointer-events: none;
  }
  .cert-title { font-family: 'Playfair Display', serif; font-size: 42px; color: #2F5233; margin: 18px 0 6px; letter-spacing: 2px; }
  .cert-sub { font-size: 13px; color: #B8860B; letter-spacing: 4px; text-transform: uppercase; margin-bottom: 28px; }
  .cert-recipient { font-family: 'Playfair Display', serif; font-size: 36px; color: #1A201E; margin: 20px 0 10px; border-bottom: 2px solid #D4AF37; display: inline-block; padding: 0 30px 8px; }
  .cert-body { font-size: 14px; color: #4A5553; max-width: 560px; margin: 16px auto; line-height: 1.9; }
  .cert-achievement { font-size: 17px; color: #2F5233; font-weight: 700; margin: 14px 0; }
  .cert-date { margin-top: 30px; font-size: 11px; color: #6b6b6b; letter-spacing: 2px; }

  @media print {
    @page { margin: 14mm; size: A4; }
    .doc-header, .doc-footer { background: #FAFAF6 !important; }
    .no-print { display: none !important; }
  }
`;

function nowIso() {
  const d = new Date();
  return d.toLocaleString("ar-EG", { year:"numeric", month:"long", day:"2-digit", hour:"2-digit", minute:"2-digit" });
}
function refNumber(prefix = "DOC") {
  const d = new Date();
  return `${prefix}-${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}${String(d.getDate()).padStart(2,"0")}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
}

/**
 * buildPremiumHTML: wraps an inner HTML body in the official Inclusion+AI document shell.
 * @param {Object} opts
 *   - title: document title (Arabic)
 *   - subtitle: small line under title
 *   - docType: type label shown in the band ("شهادة", "تقرير", ...)
 *   - subjectName: child / case name
 *   - subjectMeta: object with extra info (age, grade, ...)
 *   - body: inner HTML body
 *   - signatures: [{role, name?}]
 *   - refPrefix: ref number prefix
 *   - showSeal: include gold accreditation seal
 *   - variant: "report" (default) | "certificate"
 */
export function buildPremiumHTML({ title, subtitle = "", docType = "وثيقة", subjectName = "", subjectMeta = {}, body = "", signatures = null, refPrefix = "DOC", showSeal = true, variant = "report" }) {
  const ref = refNumber(refPrefix);
  const meta = [
    subjectName ? `<div class="info-row"><span class="label">المعني/الحالة:</span><span class="value">${subjectName}</span></div>` : "",
    ...Object.entries(subjectMeta).filter(([_, v]) => v != null && v !== "").map(([k, v]) =>
      `<div class="info-row"><span class="label">${k}:</span><span class="value">${v}</span></div>`)
  ].join("");

  const sigs = signatures && signatures.length > 0
    ? `<div class="signatures">${signatures.map(s => `<div class="sig-block"><div class="sig-line"></div><div class="role">${s.role||""}</div>${s.name?`<div class="name">${s.name}</div>`:""}</div>`).join("")}</div>`
    : "";

  const seal = showSeal ? `<div class="seal">${ACCREDITATION_BADGE_SVG}</div>` : "";

  return `<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"/><title>${title}</title><style>${SHARED_CSS}</style></head><body>
  <div class="doc ${variant==="certificate"?"cert-frame":""}">
    ${variant === "certificate" ? `<div class="cert-inner">${body}<div class="cert-date">${nowIso()} · ${ref}</div></div>` : `
    <div class="doc-header">
      <div class="brand">
        ${ACCREDITATION_BADGE_SVG}
        <div class="brand-text"><h1>${PLATFORM}</h1><p>${TAGLINE}</p></div>
      </div>
      <div class="doc-meta">
        <div>نوع الوثيقة: <strong>${docType}</strong></div>
        <div>تاريخ الإصدار: ${nowIso()}</div>
        <div>الرقم المرجعي: <span class="ref">${ref}</span></div>
      </div>
    </div>
    <div class="doc-title-band">
      <h2>${title}</h2>
      ${subtitle ? `<p>${subtitle}</p>` : ""}
    </div>
    <div class="doc-body">
      ${meta ? `<div class="section"><h3>بيانات الوثيقة</h3>${meta}</div>` : ""}
      ${body}
      ${sigs}
      ${seal}
    </div>
    <div class="doc-footer">
      <span><strong class="platform">${PLATFORM}</strong> · ${PLATFORM_AR}</span>
      <span>وثيقة رسمية · تم الإصدار آلياً عبر منصة الدمج التعليمي الذكي</span>
      <span>${ref}</span>
    </div>`}
  </div>
  </body></html>`;
}

/** Open print window with auto-print */
export function openPrintWindow(html) {
  const w = window.open("", "_blank", "noopener,noreferrer");
  if (!w) { alert("تعذّر فتح نافذة الطباعة — تأكد من إذن النوافذ المنبثقة."); return; }
  w.document.write(html);
  w.document.write(`<script>window.onload=function(){setTimeout(function(){window.print();},400);};<\/script>`);
  w.document.close();
}

/** Render HTML into an offscreen iframe, capture with html2canvas, save as multi-page PDF */
export async function exportPremiumPDF(html, filename = "document.pdf") {
  return new Promise((resolve, reject) => {
    const frame = document.createElement("iframe");
    frame.style.cssText = "position:fixed;left:-10000px;top:0;width:820px;height:1200px;border:0";
    document.body.appendChild(frame);
    frame.onload = async () => {
      try {
        // wait for webfont load
        await new Promise(r => setTimeout(r, 700));
        const target = frame.contentDocument.querySelector(".doc");
        const canvas = await html2canvas(target, { scale: 2, backgroundColor: "#ffffff", useCORS: true, windowWidth: 820 });
        const img = canvas.toDataURL("image/png");
        const pdf = new jsPDF({ orientation:"portrait", unit:"mm", format:"a4" });
        const w = pdf.internal.pageSize.getWidth();
        const h = (canvas.height * w) / canvas.width;
        const pageH = pdf.internal.pageSize.getHeight();
        if (h <= pageH) {
          pdf.addImage(img, "PNG", 0, 0, w, h);
        } else {
          let pos = 0, remaining = h;
          while (remaining > 0) {
            pdf.addImage(img, "PNG", 0, pos, w, h);
            remaining -= pageH; pos -= pageH;
            if (remaining > 0) pdf.addPage();
          }
        }
        pdf.save(filename);
        resolve();
      } catch (e) { reject(e); } finally { document.body.removeChild(frame); }
    };
    frame.srcdoc = html;
  });
}

// Convenience: build + print
export function printPremium(opts) { openPrintWindow(buildPremiumHTML(opts)); }
// Convenience: build + download
export async function downloadPremium(opts, filename) { return exportPremiumPDF(buildPremiumHTML(opts), filename); }

// ===== Curriculum Adaptation Goal Report =====
export function buildCurriculumGoalOpts(g, progress = [], certificate = null) {
  const list = (arr) => (arr || []).map(x => `<li>${x}</li>`).join("");
  const steps = (g.lesson_plan || []).map(s => `
    <tr>
      <td style="text-align:center;font-weight:700;color:#2F5233">${s.step || ""}</td>
      <td><strong>${s.title || ""}</strong></td>
      <td>${s.activity || ""}</td>
      <td style="text-align:center">${s.duration_min || 0} د</td>
    </tr>`).join("");
  const diff = g.differentiation || {};
  const progressRows = (progress || []).map(p => `
    <tr>
      <td>${new Date(p.ts).toLocaleDateString("ar-EG")}</td>
      <td>${p.performance_notes || ""}</td>
      <td style="text-align:center">${p.support_level || ""}</td>
      <td style="text-align:center">${p.independence_status || ""}</td>
      <td style="text-align:center"><span class="pill ${(p.score||0)>=80?"pill-emerald":(p.score||0)>=40?"pill-amber":"pill-rose"}">${p.score || 0}%</span></td>
    </tr>`).join("");
  const supports = g.support_strategies?.length ? g.support_strategies : g.autism_supports;
  const body = `
    <div class="section">
      <h3>الهدف الأصلي</h3>
      <div class="quote">${g.original_objective || "—"}</div>
    </div>
    <div class="section">
      <h3>الهدف بعد التكييف الذكي</h3>
      <div class="quote" style="border-right-color:#2F5233;background:#f2f6f2">${g.adapted_objective || "—"}</div>
    </div>
    <div class="grid-2">
      <div class="section">
        <h3>تحليل الهدف</h3>
        <div class="info-row"><span class="label">الهدف الأكاديمي المحلل</span><span class="value">${g.parsed_academic_target || "—"}</span></div>
        <div class="info-row"><span class="label">فرع المادة</span><span class="value">${g.subject_branch || "—"}</span></div>
        <div class="info-row"><span class="label">تكييف المرحلة</span><span class="value">${g.grade_branch || "—"}</span></div>
        <div class="info-row"><span class="label">مسار الوصول</span><span class="value">${g.access_branch || "—"}</span></div>
      </div>
      <div class="section">
        <h3>التقييم والنجاح</h3>
        <div class="info-row"><span class="label">طريقة التقييم</span><span class="value">${g.assessment_method || "—"}</span></div>
        <div class="info-row"><span class="label">معيار النجاح</span><span class="value">${g.success_criteria || "—"}</span></div>
      </div>
    </div>
    ${steps ? `<div class="section">
      <h3>خطة الدرس المكيّفة</h3>
      <table class="report">
        <thead><tr><th style="width:50px">#</th><th>عنوان الخطوة</th><th>النشاط</th><th style="width:70px">المدة</th></tr></thead>
        <tbody>${steps}</tbody>
      </table>
    </div>` : ""}
    ${supports?.length ? `<div class="section"><h3>الاستراتيجيات والدعائم</h3><ul>${list(supports)}</ul></div>` : ""}
    ${g.accommodations?.length ? `<div class="section"><h3>التعديلات والتكييفات (Accommodations)</h3><ul>${list(g.accommodations)}</ul></div>` : ""}
    ${(diff.content || diff.process || diff.product) ? `<div class="section">
      <h3>التمايز التربوي (Differentiation)</h3>
      <div class="grid-3">
        ${diff.content ? `<div class="info-row" style="flex-direction:column"><span class="label">المحتوى</span><span class="value">${diff.content}</span></div>` : ""}
        ${diff.process ? `<div class="info-row" style="flex-direction:column"><span class="label">الأسلوب</span><span class="value">${diff.process}</span></div>` : ""}
        ${diff.product ? `<div class="info-row" style="flex-direction:column"><span class="label">الناتج</span><span class="value">${diff.product}</span></div>` : ""}
      </div>
    </div>` : ""}
    ${g.assistive_tech?.length ? `<div class="section"><h3>التكنولوجيا المساندة</h3><ul>${list(g.assistive_tech)}</ul></div>` : ""}
    ${progressRows ? `<div class="section">
      <h3>سجل التقدم</h3>
      <table class="report">
        <thead><tr><th>التاريخ</th><th>ملاحظات الأداء</th><th style="width:80px">الدعم</th><th style="width:80px">الاستقلالية</th><th style="width:70px">الإتقان</th></tr></thead>
        <tbody>${progressRows}</tbody>
      </table>
    </div>` : ""}
    ${certificate ? `<div class="section">
      <h3>الشهادة الصادرة</h3>
      <div class="info-row"><span class="label">رقم الشهادة</span><span class="value" style="font-family:monospace">${certificate.id}</span></div>
      <div class="info-row"><span class="label">نسبة الإتقان</span><span class="value"><span class="pill pill-gold">${certificate.mastery_score}%</span></span></div>
      <div class="info-row"><span class="label">تاريخ الإصدار</span><span class="value">${new Date(certificate.issued_at).toLocaleString("ar-EG")}</span></div>
      <div class="info-row"><span class="label">المتخصص المعتمد</span><span class="value">${certificate.therapist || "—"}</span></div>
    </div>` : ""}
  `;
  return {
    title: "تقرير تكييف الهدف التربوي",
    subtitle: "AI-Powered Inclusive Curriculum Adaptation Report",
    docType: "تقرير تربوي رسمي",
    subjectName: g.lesson_title || "—",
    subjectMeta: {
      "المادة": g.subject || "—",
      "المرحلة/الصف": g.grade_or_stage || "—",
      "المنهج": g.curriculum_name || "—",
      "الوحدة/القسم": g.unit_or_section || "—",
      "الحالة": g.status || "—",
    },
    body,
    signatures: [{ role: "المتخصص المسؤول" }, { role: "المنسق الأكاديمي" }, { role: "ولي الأمر" }],
    refPrefix: "ADP",
  };
}
export const printCurriculumGoal = (g, progress, cert) => printPremium(buildCurriculumGoalOpts(g, progress, cert));
export const downloadCurriculumGoal = (g, progress, cert) => downloadPremium(buildCurriculumGoalOpts(g, progress, cert), `Goal-${(g.lesson_title || "adapted").replace(/[^\p{L}\p{N}_-]+/gu, "_")}.pdf`);

// ===== Goal Mastery Certificate (Gold-bordered official) =====
export function buildGoalCertificateOpts(cert) {
  const body = `
    <div style="font-size:14px;color:#4A5553;letter-spacing:1px;margin-bottom:8px">CERTIFICATE OF MASTERY</div>
    <div class="cert-title">شهادة إتقان هدف تربوي</div>
    <div class="cert-sub">Inclusion+AI · تكييف المناهج الشامل</div>
    <div style="font-size:14px;color:#4A5553">تشهد منصة Inclusion+AI بأنّ الطالب/ة</div>
    <div class="cert-recipient">${cert.child_name || cert.therapist || "المتعلم"}</div>
    <div class="cert-body">
      قد أتمّ بنجاح الهدف التربوي المكيّف ضمن مسار التعليم الدامج،<br/>
      وحقّق معايير الإتقان المعتمدة وفق الممارسات العلمية للتربية الخاصة والتعليم الشامل.
    </div>
    <div class="cert-achievement">«${cert.lesson_title || ""}»</div>
    <div style="margin-top:14px;font-size:12px;color:#4A5553">
      <div><strong>الهدف الأصلي:</strong> ${cert.original_objective || "—"}</div>
      <div style="margin-top:6px"><strong>الهدف بعد التكييف:</strong> ${cert.adapted_objective || "—"}</div>
    </div>
    <div style="margin-top:20px;display:inline-block;padding:8px 22px;background:linear-gradient(135deg,#FBE69A 0%,#D4AF37 100%);color:#5C4400;border-radius:20px;font-weight:800;font-size:16px">نسبة الإتقان: ${cert.mastery_score || 0}%</div>
    <div style="margin-top:30px;display:grid;grid-template-columns:1fr 1fr;gap:32px;max-width:520px;margin-left:auto;margin-right:auto">
      <div style="text-align:center;border-top:1px solid #2F5233;padding-top:8px">
        <div style="font-size:11px;color:#2F5233;font-weight:700">المتخصص المعتمد</div>
        <div style="font-size:10px;color:#6b6b6b;margin-top:2px">${cert.therapist || "—"}</div>
      </div>
      <div style="text-align:center;border-top:1px solid #2F5233;padding-top:8px">
        <div style="font-size:11px;color:#2F5233;font-weight:700">رقم الشهادة</div>
        <div style="font-size:10px;color:#6b6b6b;margin-top:2px;font-family:monospace">${cert.id || "—"}</div>
      </div>
    </div>
  `;
  return { title: "شهادة إتقان هدف", docType: "شهادة رسمية", body, variant: "certificate", refPrefix: "MST", showSeal: false };
}
export const printGoalCertificate = (cert) => printPremium(buildGoalCertificateOpts(cert));
export const downloadGoalCertificate = (cert) => downloadPremium(buildGoalCertificateOpts(cert), `Mastery-Certificate-${cert.id || Date.now()}.pdf`);
